import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import { LandingPage } from './pages/LandingPage';
import { BootstrapAdminPage } from './pages/BootstrapAdminPage';
import { LoginPage } from './pages/LoginPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReportingOverviewPage } from './pages/reporting/ReportingOverviewPage';
import { EquipementsReportPage } from './pages/reporting/EquipementsReportPage';
import { InfrastructureReportPage } from './pages/reporting/InfrastructureReportPage';
import { InterventionsReportPage } from './pages/reporting/InterventionsReportPage';
import { ExportJobsPage } from './pages/reporting/ExportJobsPage';
import { PrestataireReportPage } from './pages/reporting/PrestataireReportPage';
import { ContratsPage } from './pages/ContratsPage';
import { ProfilePage } from './pages/ProfilePage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminRolesPage } from './pages/AdminRolesPage';
import { AdminAuditLogsPage } from './pages/AdminAuditLogsPage';
import { AdminFormBuilderPage } from './pages/AdminFormBuilderPage';
import { AdminSettingsPage } from './pages/admin/AdminSettingsPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { PersonnelsListPage } from './pages/PersonnelsListPage';
import { PersonnelDetailPage } from './pages/PersonnelDetailPage';
import { BatimentsPage } from './pages/BatimentsPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { ServicesPage } from './pages/ServicesPage';
import { EquipementsPage } from './pages/EquipementsPage';
import { MesAffectationsPage } from './pages/MesAffectationsPage';
import { InterventionsPage } from './pages/InterventionsPage';
import { InterventionDashboardPage } from './pages/InterventionDashboardPage';
import { InterventionAgendaPage } from './pages/InterventionAgendaPage';
import { UrgencesPage } from './pages/UrgencesPage';
import { MesValidationsPage } from './pages/MesValidationsPage';
import { MesInterventionsPage } from './pages/MesInterventionsPage';
import { PrestatairesPage } from './pages/PrestatairesPage';
import { TenantsPage } from './pages/platform/TenantsPage';
import { DemoRequestsPage } from './pages/platform/DemoRequestsPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { TenantSettingsProvider } from './contexts/TenantSettingsContext';

/**
 * Routes applicatives.
 *
 * Routes livrées :
 *  - `/`                    — Landing page publique (US-DASH-02).
 *  - `/my-secret-admin`     — Bootstrap super-admin (US-AUTH-05).
 *  - `/login`               — Connexion (US-AUTH-02).
 *  - `/forgot-password`     — Demande de réinitialisation (US-AUTH-03).
 *  - `/reset-password`      — Saisie du nouveau mdp via token (US-AUTH-03).
 *  - `/dashboard`           — Placeholder (sera enrichi en Phase 12).
 *  - `/profil`              — Profil personnel (US-AUTH-04).
 *  - `/notifications`       — Centre complet de notifications (US-NOTIF-03).
 *  - `/admin/utilisateurs`  — Gestion des utilisateurs (US-ADM-02).
 *  - `/admin/roles`         — Rôles & permissions (US-ADM-03).
 *  - `/admin/audit-logs`    — Journaux d'audit (US-ADM-04).
 *  - `/admin/formulaires`   — Form Builder (US-ADM-08).
 *
 * Layout applicatif (sidebar/header) injecté par `<ProtectedRoute>` (US-DASH-03).
 * `<NotificationsProvider>` maintient une connexion WebSocket persistante
 * (US-NOTIF-01) dès qu'un utilisateur est authentifié.
 */
export function App() {
  return (
    <AuthProvider>
      <TenantSettingsProvider>
        <NotificationsProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </NotificationsProvider>
      </TenantSettingsProvider>
      {/* US-TR-03 — toasts UI globaux. Style cohérent avec le design system :
          succès vert, erreur rouge, position top-right, durée 4s par défaut. */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          className: 'text-sm',
          success: {
            iconTheme: { primary: '#20947C', secondary: 'white' },
          },
          error: {
            duration: 6000,
            iconTheme: { primary: '#dc2626', secondary: 'white' },
          },
        }}
      />
    </AuthProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/my-secret-admin" element={<BootstrapAdminPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting"
        element={
          <ProtectedRoute module="reporting" action="read">
            <ReportingOverviewPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/equipements"
        element={
          <ProtectedRoute module="reporting" action="read">
            <EquipementsReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/infrastructure"
        element={
          <ProtectedRoute module="reporting" action="read">
            <InfrastructureReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/interventions"
        element={
          <ProtectedRoute module="reporting" action="read">
            <InterventionsReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/prestataires"
        element={
          <ProtectedRoute module="reporting" action="read">
            <PrestataireReportPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reporting/historique"
        element={
          <ProtectedRoute module="reporting" action="read">
            <ExportJobsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/personnels"
        element={
          <ProtectedRoute module="personnels" action="read">
            <PersonnelsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/personnels/:id"
        element={
          <ProtectedRoute module="personnels" action="read">
            <PersonnelDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/batiments"
        element={
          <ProtectedRoute module="batiments" action="read">
            <BatimentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/batiments/reservations"
        element={
          <ProtectedRoute module="batiments" action="read">
            <ReservationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/services"
        element={
          <ProtectedRoute module="services" action="read">
            <ServicesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/equipements"
        element={
          <ProtectedRoute module="equipements" action="read">
            <EquipementsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mes-affectations"
        element={
          <ProtectedRoute module="equipements" action="read">
            <MesAffectationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions"
        element={
          <ProtectedRoute module="interventions" action="read">
            <InterventionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions/agenda"
        element={
          <ProtectedRoute module="interventions" action="read">
            <InterventionAgendaPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions/dashboard"
        element={
          <ProtectedRoute module="interventions" action="read">
            <InterventionDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interventions/urgences"
        element={
          <ProtectedRoute module="interventions" action="read">
            <UrgencesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mes-validations"
        element={
          <ProtectedRoute module="interventions" action="read">
            <MesValidationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mes-interventions"
        element={
          <ProtectedRoute module="interventions" action="read">
            <MesInterventionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/prestataires"
        element={
          <ProtectedRoute module="interventions" action="update">
            <PrestatairesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contrats"
        element={
          <ProtectedRoute module="interventions" action="read">
            <ContratsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/utilisateurs"
        element={
          <ProtectedRoute module="administration" action="manage">
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute module="administration" action="manage">
            <AdminRolesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <ProtectedRoute module="administration" action="read">
            <AdminAuditLogsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/formulaires"
        element={
          <ProtectedRoute module="administration" action="manage">
            <AdminFormBuilderPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/parametres/*"
        element={
          <ProtectedRoute module="administration" action="manage">
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/tenants"
        element={
          <ProtectedRoute>
            <TenantsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/demo-requests"
        element={
          <ProtectedRoute>
            <DemoRequestsPage />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
